import React from 'react';
import {
    Show,
    useTranslate,
    useRecordContext,
    useRedirect,
    EditButton,
    DeleteButton,
    TopToolbar,
    ListButton,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    IconButton,
    Divider,
    Alert,
} from '@mui/material';
import { ArrowBack, Rule, Star } from '@mui/icons-material';
import { useCanWritePolicies } from './useCanWritePolicies';

const ShowActions = () => {
    const record = useRecordContext();
    const isSystemDefault = !!(record?.is_system_default ?? record?.isSystemDefault);
    const canWrite = useCanWritePolicies();
    return (
        <TopToolbar>
            <ListButton />
            {canWrite && <EditButton />}
            {canWrite && !isSystemDefault && <DeleteButton />}
        </TopToolbar>
    );
};

const PageHeader: React.FC = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    if (!record) return null;
    const isSystemDefault = !!(record.is_system_default ?? record.isSystemDefault);
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 4 } }}>
            <IconButton
                onClick={() => redirect('/reservation-policies')}
                size="large"
                sx={{ backgroundColor: '#FFFFFF', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                title={translate('resources.reservation-policies.list.title')}
            >
                <ArrowBack />
            </IconButton>
            <Rule sx={{ color: '#009A75', fontSize: 32 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2rem' } }}>
                        {record.name || translate('resources.reservation-policies.show.subtitle')}
                    </Typography>
                    {isSystemDefault && (
                        <Chip
                            size="small"
                            color="warning"
                            icon={<Star fontSize="small" />}
                            label={translate('resources.reservation-policies.fields.isSystemDefault')}
                        />
                    )}
                </Box>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', color: 'text.secondary' }}>
                    {String(record.id)}
                </Typography>
            </Box>
        </Box>
    );
};

const fmt = (v: any): string => {
    if (v === null || v === undefined) return '-';
    if (typeof v === 'boolean') return v ? '✓' : '✗';
    if (Array.isArray(v)) return v.length === 0 ? '-' : v.join(', ');
    return String(v);
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, py: 0.75, borderBottom: '1px solid #F0F0F0' }}>
        <Box sx={{ minWidth: { xs: '100%', sm: 220 }, color: 'text.secondary', fontSize: '0.875rem' }}>
            {label}
        </Box>
        <Box sx={{ flex: 1, fontSize: '0.875rem' }}>{children}</Box>
    </Box>
);

const DetailsCard: React.FC = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record) return null;
    const feePolicy = record.feePolicy;
    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    {translate('resources.reservation-policies.sections.identity')}
                </Typography>
                <Row label={translate('resources.reservation-policies.fields.name')}>
                    {fmt(record.name)}
                </Row>
                <Row label={translate('resources.reservation-policies.fields.description')}>
                    {fmt(record.description)}
                </Row>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {translate('resources.reservation-policies.sections.timing')}
                </Typography>
                <Row label={translate('resources.reservation-policies.fields.reservationMode')}>
                    {translate(`resources.reservation-policies.modes.${record.reservationMode}`, { _: '-' })}
                </Row>
                <Row label={translate('resources.reservation-policies.fields.accessMode')}>
                    {translate(`resources.reservation-policies.accessModes.${record.accessMode}`, { _: '-' })}
                </Row>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {translate('resources.reservation-policies.sections.timingWindows')}
                </Typography>
                <Row label={translate('resources.reservation-policies.fields.slotDurationMinutes')}>
                    {record.reservationMode === 'next_slot'
                        ? translate('resources.reservation-policies.helpers.slotDurationDisabled')
                        : fmt(record.slotDurationMinutes)}
                </Row>
                <Row label={translate('resources.reservation-policies.fields.slotGranularityMinutes')}>{fmt(record.slotGranularityMinutes)}</Row>
                <Row label={translate('resources.reservation-policies.fields.holdWindowMinutes')}>{fmt(record.holdWindowMinutes)}</Row>
                <Row label={translate('resources.reservation-policies.fields.leadTimeMinutes')}>
                    {record.reservationMode === 'next_slot'
                        ? translate('resources.reservation-policies.helpers.naNextSlot')
                        : fmt(record.leadTimeMinutes)}
                </Row>
                <Row label={translate('resources.reservation-policies.fields.cancelWindowMinutes')}>
                    {record.reservationMode === 'next_slot'
                        ? translate('resources.reservation-policies.helpers.naNextSlot')
                        : fmt(record.cancelWindowMinutes)}
                </Row>
                <Row label={translate('resources.reservation-policies.fields.modifyWindowMinutes')}>
                    {record.reservationMode === 'next_slot'
                        ? translate('resources.reservation-policies.helpers.naNextSlot')
                        : fmt(record.modifyWindowMinutes)}
                </Row>
                <Row label={translate('resources.reservation-policies.fields.maxAdvanceDays')}>
                    {record.reservationMode === 'next_slot'
                        ? translate('resources.reservation-policies.helpers.naNextSlot')
                        : fmt(record.maxAdvanceDays)}
                </Row>
                <Row label={translate('resources.reservation-policies.fields.maxActivePerUser')}>{fmt(record.maxActivePerUser)}</Row>
                <Row label={translate('resources.reservation-policies.fields.maxModifications')}>
                    {record.reservationMode === 'next_slot'
                        ? translate('resources.reservation-policies.helpers.naNextSlot')
                        : fmt(record.maxModifications)}
                </Row>
                <Row label={translate('resources.reservation-policies.fields.liveStatusHorizonHours')}>{fmt(record.liveStatusHorizonHours)}</Row>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {translate('resources.reservation-policies.sections.feeBehavior')}
                </Typography>
                <Alert severity="info" sx={{ mb: 1 }}>
                    {translate('resources.reservation-policies.banners.feeBehavior')}
                </Alert>
                <Row label={translate('resources.reservation-policies.fields.feePolicy')}>
                    {feePolicy ? translate(`resources.reservation-policies.feePolicies.${feePolicy}`, { _: feePolicy }) : '-'}
                </Row>
                <Row label={translate('resources.reservation-policies.fields.blockedNoshowToleranceMinutes')}>
                    {record.reservationMode === 'next_slot'
                        ? translate('resources.reservation-policies.helpers.naNextSlot')
                        : fmt(record.blockedNoshowToleranceMinutes)}
                </Row>
                <Row label={translate('resources.reservation-policies.fields.forfeitOnBlockedNoShow')}>
                    {record.reservationMode === 'next_slot'
                        ? translate('resources.reservation-policies.helpers.naNextSlot')
                        : record.forfeitOnBlockedNoShow
                            ? translate('ra.boolean.true')
                            : translate('ra.boolean.false')}
                </Row>
                <Row label={translate('resources.reservation-policies.fields.blockedDetectionIncrementMinutes')}>
                    {record.reservationMode === 'next_slot'
                        ? translate('resources.reservation-policies.helpers.naNextSlot')
                        : fmt(record.blockedDetectionIncrementMinutes)}
                </Row>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {translate('resources.reservation-policies.sections.eligibility')}
                </Typography>
                <Row label={translate('resources.reservation-policies.fields.allowedMembershipLevels')}>
                    {fmt(record.allowedMembershipLevels)}
                </Row>
                <Row label={translate('resources.reservation-policies.fields.allowedGroupIds')}>
                    {fmt(record.allowedGroupIds)}
                </Row>
            </CardContent>
        </Card>
    );
};

export const PolicyShow = () => (
    <Show component="div" title={false} actions={<ShowActions />}>
        <Box sx={{ width: '100%', maxWidth: 1200, margin: '0 auto', p: { xs: 1, sm: 2 } }}>
            <PageHeader />
            <DetailsCard />
        </Box>
    </Show>
);

export default PolicyShow;
